import React, { useMemo, useState } from 'react';
import {
  Play, Copy, Check, RotateCcw, ChevronDown, ChevronRight, Terminal, MonitorPlay, Code2,
} from 'lucide-react';
import useDemoSession from './useDemoSession';
import { req, toCurl, INITIAL_VARIABLES, STEP2_VARIABLES, STEP2_ROWS } from './wizardActions';

const API_BASE = (process.env.REACT_APP_SERVER_URL || '').replace(/\/$/, '') || window.location.origin;

/* ------------------------------ small parts ------------------------------ */

function CopyButton({ text, label = 'Copy' }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="btn btn-ghost btn-sm"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch (_) { /* clipboard blocked on http — the text is still selectable */ }
      }}
    >
      {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? 'Copied' : label}
    </button>
  );
}

// One live screen. This is a real paired device rendering through the real
// display client — the same code a smart TV loads from tv.puttv.io — not a
// preview component. Nothing in the wizard is simulated, and this is the part
// that would be most tempting to fake.
function ScreenFrame({ screen, index, label }) {
  const src = `/display?identity=${encodeURIComponent(`${screen.deviceUUID}.${screen.deviceSecret}`)}`;
  return (
    <div className="wz-screen">
      <div className="wz-screen-head">
        <span className="status-dot on" />
        <span className="wz-screen-name">{label || screen.name || `Demo screen ${index + 1}`}</span>
        <span className="mono">{screen.deviceUUID.slice(0, 8)}</span>
      </div>
      <div className="wz-screen-body">
        <iframe
          src={src}
          title={`Demo screen ${index + 1}`}
          className="wz-iframe"
          // Same-origin so the display client can reach the API and its socket;
          // this is our own route, not third-party content.
          sandbox="allow-scripts allow-same-origin"
        />
      </div>
    </div>
  );
}

function EventLog({ events }) {
  if (!events.length) return null;
  return (
    <div className="wz-log">
      <div className="wz-log-head">
        <Terminal size={14} /> Wire log
        <span className="wz-log-count">{events.length}</span>
      </div>
      <div className="wz-log-body">
        {[...events].reverse().map((e, i) => (
          <div key={events.length - i} className={`wz-log-line ${e.kind}`}>
            <span className={`wz-status s-${String(e.status)[0] || '0'}`}>
              {e.status === 0 ? 'ERR' : e.status}
            </span>
            <span className="wz-log-method">{e.method}</span>
            <span className="wz-log-path">{e.path}</span>
            <span className="wz-log-ms">{e.ms}ms</span>
            <span className="wz-log-label">{e.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function JsonBlock({ label, value, tall }) {
  const text = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  return (
    <div className="wz-json">
      <div className="wz-json-head">
        <span className="label">{label}</span>
        <CopyButton text={text} />
      </div>
      <div className={`code-block wz-curl${tall ? ' wz-json-tall' : ''}`}>{text}</div>
    </div>
  );
}

/**
 * A step's example, in two views over the SAME request descriptors.
 *
 * Default is the payload — the template and the variables — because that is
 * what someone is actually deciding about, and it makes the product look as
 * simple as it is. The full curl (headers, key, escaping) is one click away for
 * anyone who wants to run it, but leading with it buries the idea in transport.
 *
 * Both views and the Run button come from the same `requests`, so the thing
 * shown and the thing sent cannot drift — the moment they could, the demo stops
 * being evidence.
 */
function ExamplePanel({ simple, requests, apiKey, onRun, busy, result, runLabel = 'Run', note }) {
  const [view, setView] = useState('simple');
  const curl = useMemo(
    () => requests.map((r) => toCurl(r, { base: API_BASE, key: apiKey })).join('\n\n'),
    [requests, apiKey]
  );

  return (
    <div className="wz-req">
      <div className="wz-req-head">
        <div className="wz-tabs" role="tablist">
          <button role="tab" aria-selected={view === 'simple'}
            className={view === 'simple' ? 'active' : ''} onClick={() => setView('simple')}>
            Template &amp; variables
          </button>
          <button role="tab" aria-selected={view === 'code'}
            className={view === 'code' ? 'active' : ''} onClick={() => setView('code')}>
            <Code2 size={13} /> Full code
          </button>
        </div>
        <div className="wz-req-actions">
          {view === 'code' && <CopyButton text={curl} />}
          <button className="btn btn-primary btn-sm" onClick={onRun} disabled={busy}>
            <Play size={14} /> {busy ? 'Running…' : runLabel}
          </button>
        </div>
      </div>

      {view === 'simple' ? (
        <>
          {simple.map((b) => <JsonBlock key={b.label} {...b} />)}
          {note && <p className="props-hint wz-note">{note}</p>}
        </>
      ) : (
        <div className="code-block wz-curl">{curl}</div>
      )}

      {result && (
        <div className={`wz-result ${result.ok ? 'ok' : 'fail'}`}>
          <span className={`wz-status s-${String(result.status)[0] || '0'}`}>
            {result.status === 0 ? 'ERR' : result.status}
          </span>
          <span className="wz-result-ms">{result.ms}ms round trip</span>
          <pre className="wz-result-body">
            {result.error || JSON.stringify(result.body, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}

/* -------------------------------- drawer -------------------------------- */

// Everything that didn't earn a step. The rule for what lives here: walk people
// through what they wouldn't believe without seeing it; list what they'd accept
// from one line of docs. Playlists rotate and CEC turns TVs on — nobody doubts
// that. So these are one click each, against the screens still live above,
// rather than four more steps standing between someone and the exit.
function Drawer({ session, runStep, busyKey }) {
  const [open, setOpen] = useState(false);
  const { screens, templateId, sourceId, apiKey } = session;
  const first = screens[0];

  const entries = useMemo(() => {
    if (!first) return [];
    return [
      {
        id: 'per-screen',
        title: 'Same template, different content per screen',
        blurb: 'One saved template, two screens, different variables. This is how one '
             + 'template serves every site you run.',
        request: req.devicePush(first.deviceUUID, templateId, {
          headline: 'Store 42 — Newtown',
          tagline: 'Per-screen variables, same template id',
        }),
      },
      {
        id: 'playlist',
        title: 'Rotate a playlist',
        blurb: 'Three items on a 5-second loop. Survives reconnects and keeps running '
             + 'until you delete it.',
        request: req.setPlaylist(first.deviceUUID, [
          { template_id: templateId, duration_seconds: 5, variables: { headline: 'Playlist item 1', tagline: 'Rotating every 5s' } },
          { template_id: templateId, duration_seconds: 5, variables: { headline: 'Playlist item 2', tagline: 'Each item has its own variables' } },
          { template_id: templateId, duration_seconds: 5, variables: { headline: 'Playlist item 3', tagline: 'Loops forever' } },
        ]),
      },
      {
        id: 'stop-playlist',
        title: 'Stop the rotation',
        blurb: 'Deleting the playlist leaves the screen on whatever it was showing.',
        request: req.stopPlaylist(first.deviceUUID),
      },
      {
        id: 'list',
        title: 'List your screens',
        blurb: 'The demo screens appear here with is_demo: true — they are real device '
             + 'rows, just short-lived ones.',
        request: req.listDevices(),
      },
      {
        id: 'bad-key',
        title: 'What a bad key does',
        blurb: 'Fires with a deliberately wrong key. 401, no detail leaked about what '
             + 'exists behind it.',
        request: req.badKey(),
      },
      {
        id: 'not-found',
        title: "Someone else's screen",
        blurb: 'A device outside your account answers 404, not 403 — so a key cannot be '
             + 'used to discover which screens exist on other accounts.',
        request: req.missingDevice(),
      },
    ];
  }, [first, templateId]);

  if (!first) return null;

  return (
    <div className="wz-drawer">
      <button className="wz-drawer-toggle" onClick={() => setOpen((v) => !v)}>
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        See more features
        <span className="wz-drawer-hint">
          {open ? '' : 'playlists, per-screen variables, the error model, hardware'}
        </span>
      </button>

      {open && (
        <div className="wz-drawer-body">
          <p className="props-hint" style={{ marginBottom: 14 }}>
            Each of these runs against the two screens above — they are still live.
          </p>

          {entries.map((e) => (
            <div key={e.id} className="wz-drawer-item">
              <div className="wz-drawer-item-head">
                <h4>{e.title}</h4>
                <button
                  className="btn btn-sm"
                  disabled={busyKey === e.id}
                  onClick={() => runStep(e.id, e.request)}
                >
                  <Play size={13} /> {busyKey === e.id ? 'Running…' : 'Run'}
                </button>
              </div>
              <p className="props-hint">{e.blurb}</p>
              <div className="code-block wz-curl wz-curl-sm">
                {toCurl(e.request, { base: API_BASE, key: apiKey })}
              </div>
            </div>
          ))}

          <div className="wz-hardware">
            <h4>Needs a physical device</h4>
            <p className="props-hint">
              These can't be demonstrated in a browser frame, so they aren't. The calls are
              real; they need a Pi (or any HDMI-CEC TV) on the other end.
            </p>
            <ul className="wz-hardware-list">
              <li>
                <code>POST /api/v1/devices/:id/power</code>
                <span>Turn the TV itself on or off over HDMI-CEC — screens wake for
                  opening time and sleep after close.</span>
              </li>
              <li>
                <code>Flash &amp; go</code>
                <span>One pre-built image; the device boots straight into pairing mode with
                  no per-device config.</span>
              </li>
              <li>
                <code>OTA updates</code>
                <span>Push a new OS image to a fleet without touching the hardware.</span>
              </li>
              <li>
                <code>Floor plan</code>
                <span>Place screens on a map of your space and push by clicking a location.
                  It's a real page in this dashboard — open <b>Floor plan</b> in the sidebar;
                  your demo screens are already on the account.</span>
              </li>
            </ul>
          </div>

          {sourceId && (
            <p className="props-hint" style={{ marginTop: 14 }}>
              Data source <code>{sourceId.slice(0, 8)}</code> is push-only for this demo.
              A polled source is the same object with a real <code>url</code> and a short{' '}
              <code>refresh_seconds</code>.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

/* -------------------------------- wizard -------------------------------- */

export default function Wizard({ userId, userEmail, onExit }) {
  const session = useDemoSession({ userId, userEmail });
  const { phase, screens, apiKey, templateId, templateJson, sourceId, group, events, run, error, progress } = session;

  const [done, setDone] = useState({});     // stepId -> true
  const [results, setResults] = useState({}); // stepId -> wire result
  const [busyKey, setBusyKey] = useState(null);

  const runStep = async (id, request) => {
    setBusyKey(id);
    const result = await run(request, apiKey);
    setResults((r) => ({ ...r, [id]: result }));
    if (result.ok) setDone((d) => ({ ...d, [id]: true }));
    setBusyKey(null);
    return result;
  };

  // Step 2 makes two different kinds of live change, so it runs both calls and
  // reports the pair — splitting them would make it two steps, which is exactly
  // what this wizard is trying not to be.
  const runStep2 = async () => {
    setBusyKey('live');
    const varsResult = await run(req.setVariables(screens[0].deviceUUID, STEP2_VARIABLES), apiKey);
    const dataResult = await run(req.pushData(sourceId, STEP2_ROWS), apiKey);
    setResults((r) => ({ ...r, live: varsResult, 'live-data': dataResult }));
    if (varsResult.ok || dataResult.ok) setDone((d) => ({ ...d, live: true }));
    setBusyKey(null);
  };

  if (phase === 'provisioning' || phase === 'idle') {
    return (
      <div className="wz">
        <WizardHead />
        <div className="wz-boot">
          <div className="wz-boot-screens">
            <div className="skel wz-boot-skel" />
            <div className="skel wz-boot-skel" />
          </div>
          <p className="props-hint">{progress || 'Setting up two live screens…'}</p>
          <p className="props-hint wz-boot-note">
            No hardware, no sign-up step, no QR to scan — these are real devices on your
            account, created and paired for you. They expire in a few hours.
          </p>
        </div>
      </div>
    );
  }

  if (phase === 'error' || phase === 'disabled') {
    return (
      <div className="wz">
        <WizardHead />
        <div className="empty">
          <p style={{ marginBottom: 12 }}>{error}</p>
          {phase === 'error' && (
            <button className="btn" onClick={session.restart}>
              <RotateCcw size={15} /> Try again
            </button>
          )}
        </div>
      </div>
    );
  }

  const step1 = req.groupPush(group, templateId);

  return (
    <div className="wz">
      <WizardHead onRestart={session.restart} />

      <div className="wz-main">
        {/* Live screens — always visible, every action changes them */}
        <div className="wz-screens">
          {screens.map((s, i) => (
            <ScreenFrame key={s.deviceUUID} screen={s} index={i} />
          ))}
        </div>

        {/* Steps */}
        <div className="wz-steps">
          <Step
            n={1}
            title="Push to both screens at once"
            done={done.push}
            blurb={
              <>
                One call to a <b>group</b>, not a device. Both screens are already in{' '}
                <code>{group}</code>, so this is what changing every screen in a building
                looks like — the same amount of work as changing one.
              </>
            }
          >
            <ExamplePanel
              simple={[
                { label: 'The template', value: templateJson, tall: true },
                { label: 'The variables filled into it', value: INITIAL_VARIABLES },
              ]}
              note={<>Saved once, then pushed by id — so the call itself is just{' '}
                <code>{`{ "template_id": "…" }`}</code>. Switch to <b>Full code</b> for the
                exact request.</>}
              requests={[step1]}
              apiKey={apiKey}
              busy={busyKey === 'push'}
              result={results.push}
              onRun={() => runStep('push', step1)}
            />
          </Step>

          <Step
            n={2}
            title="Change what's on screen without pushing again"
            done={done.live}
            locked={!done.push}
            blurb={
              <>
                A screen is a live surface, not a document. These two calls change what the
                screens show while the template stays exactly where it is —{' '}
                <b>variables</b> are per-screen, <b>data source rows</b> are shared by every
                screen bound to them.
              </>
            }
          >
            <ExamplePanel
              simple={[
                { label: 'New variables → screen 1 only', value: STEP2_VARIABLES },
                { label: 'New rows → the data source, so every screen bound to it', value: STEP2_ROWS, tall: true },
              ]}
              note="That's the whole payload — no template anywhere in either call."
              requests={[
                req.setVariables(screens[0].deviceUUID, STEP2_VARIABLES),
                req.pushData(sourceId, STEP2_ROWS),
              ]}
              apiKey={apiKey}
              busy={busyKey === 'live'}
              result={results.live}
              runLabel="Run both"
              onRun={runStep2}
            />
            {results.live && (
              <p className="props-hint" style={{ marginTop: 10 }}>
                Screen 1's headline changed; the table updated on <b>both</b>. Neither call
                re-sent the template.
              </p>
            )}
          </Step>

          {done.live && (
            <ExitRamp
              session={session}
              onExit={onExit}
            />
          )}

          <Drawer session={session} runStep={runStep} busyKey={busyKey} />
        </div>
      </div>

      <EventLog events={events} />
    </div>
  );
}

function WizardHead({ onRestart }) {
  return (
    <div className="page-head">
      <div className="page-title">
        <h2><MonitorPlay size={19} style={{ verticalAlign: -3, marginRight: 8 }} />
          See what this can do</h2>
        <p>Two live screens, real API calls, about ninety seconds. Nothing here is simulated.</p>
      </div>
      {onRestart && (
        <div className="page-actions">
          <button className="btn btn-ghost btn-sm" onClick={onRestart}>
            <RotateCcw size={14} /> Start over
          </button>
        </div>
      )}
    </div>
  );
}

function Step({ n, title, blurb, children, done, locked }) {
  return (
    <section className={`wz-step${done ? ' done' : ''}${locked ? ' locked' : ''}`}>
      <div className="wz-step-head">
        <span className="wz-step-n">{done ? <Check size={15} /> : n}</span>
        <h3>{title}</h3>
      </div>
      <p className="wz-step-blurb">{blurb}</p>
      {children}
    </section>
  );
}

// What someone leaves with: their real key, their real ids, and a runnable
// starter. The wizard is worthless if it ends at a "Done!" screen — the point
// is to hand over something that already works in their own terminal.
function ExitRamp({ session, onExit }) {
  const { apiKey, screens, templateId, sourceId, group } = session;
  const [lang, setLang] = useState('bash');

  const snippet = useMemo(() => {
    const dev = screens[0] ? screens[0].deviceUUID : 'DEVICE_UUID';
    if (lang === 'node') {
      return `const KEY = ${JSON.stringify(apiKey)};
const BASE = ${JSON.stringify(API_BASE)};

const call = (path, method = 'GET', body) =>
  fetch(BASE + path, {
    method,
    headers: { 'X-API-Key': KEY, 'Content-Type': 'application/json' },
    body: body && JSON.stringify(body),
  }).then((r) => r.json());

// Change what every screen in the group shows
await call('/api/v1/groups/${group}/push', 'POST', { template_id: '${templateId}' });

// Change one value, live, without re-pushing
await call('/api/v1/devices/${dev}/variables', 'PUT', {
  variables: { headline: 'Anything you like' },
});`;
    }
    if (lang === 'python') {
      return `import requests

KEY  = ${JSON.stringify(apiKey)}
BASE = ${JSON.stringify(API_BASE)}
H    = {"X-API-Key": KEY, "Content-Type": "application/json"}

# Change what every screen in the group shows
requests.post(f"{BASE}/api/v1/groups/${group}/push",
              headers=H, json={"template_id": "${templateId}"})

# Change one value, live, without re-pushing
requests.put(f"{BASE}/api/v1/devices/${dev}/variables",
             headers=H, json={"variables": {"headline": "Anything you like"}})`;
    }
    return `export PUTTV_KEY=${apiKey}
export PUTTV_BASE=${API_BASE}
export DEV=${dev}

# Change what every screen in the group shows
curl -X POST $PUTTV_BASE/api/v1/groups/${group}/push \\
  -H "X-API-Key: $PUTTV_KEY" -H "Content-Type: application/json" \\
  -d '{"template_id":"${templateId}"}'

# Change one value, live, without re-pushing
curl -X PUT $PUTTV_BASE/api/v1/devices/$DEV/variables \\
  -H "X-API-Key: $PUTTV_KEY" -H "Content-Type: application/json" \\
  -d '{"variables":{"headline":"Anything you like"}}'`;
  }, [lang, apiKey, screens, templateId, group]);

  return (
    <section className="wz-exit">
      <h3>That's the whole loop.</h3>
      <p className="wz-step-blurb">
        Everything above ran against the public API with the key below — no dashboard-only
        endpoints, nothing privileged. Paste this into your own terminal and it works right
        now, against the screens still open at the top of this page.
      </p>

      <div className="wz-lang">
        {['bash', 'node', 'python'].map((l) => (
          <button
            key={l}
            className={`btn btn-sm${lang === l ? ' btn-primary' : ' btn-ghost'}`}
            onClick={() => setLang(l)}
          >
            {l}
          </button>
        ))}
        <div style={{ marginLeft: 'auto' }}><CopyButton text={snippet} /></div>
      </div>
      <div className="code-block wz-curl">{snippet}</div>

      <div className="wz-facts">
        <div><span className="label">API key</span><code className="mono">{apiKey}</code></div>
        <div><span className="label">Template</span><code className="mono">{templateId}</code></div>
        <div><span className="label">Group</span><code className="mono">{group}</code></div>
        {sourceId && <div><span className="label">Data source</span><code className="mono">{sourceId}</code></div>}
      </div>

      <p className="props-hint">
        The key is a real scoped key — it reaches only your own screens, groups and
        templates. Revoke it any time in <b>Settings → API keys</b>; it's listed as
        "Wizard (demo)". The demo screens expire on their own in a few hours.
      </p>

      <div className="wz-exit-actions">
        <a className="btn" href={`${API_BASE}/api/docs`} target="_blank" rel="noopener noreferrer">
          Full API reference
        </a>
        {onExit && (
          <button className="btn btn-ghost" onClick={onExit}>
            Go to my screens
          </button>
        )}
      </div>
    </section>
  );
}
